import { useRef, useState, useEffect } from "react";
import ReactTooltip from "react-tooltip";
import {
  QuestionIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@primer/octicons-react";
import MetaTags from "./MetaTag";
import { NavLink } from "react-router-dom";

export default function Pricing() {
  const [toggle, setToggle] = useState(false);
  const handleToggle = () => {
    setToggle(!toggle);
  };
  const tableRef = useRef(null);
  const headerRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [width, setWidth] = useState(window?.innerWidth);

  useEffect(() => {
    const updateWindowDimensions = () => {
      const newHeight = window.innerWidth;
      setWidth(newHeight);
    };

    window.addEventListener("resize", updateWindowDimensions);
    return () => window.removeEventListener("resize", updateWindowDimensions);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) {
        const divRect = tableRef.current.getBoundingClientRect();
        const headerRect = headerRef.current.getBoundingClientRect();
        // Compare window scroll position with the top position of the div
        if (divRect.top < 10 && divRect.bottom > 150) {
          setIsSticky(true);
          headerRect.width = divRect.width;
        } else {
          setIsSticky(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [tableRef, toggle]);

  const planAndPricingTable = {
    basic: [
      {
        plan: "Monthly Price",
        free: "$10",
        starter: "$40",
        gettingSerious: "$65",
        fullTilt: "$135",
        tooltip: false,
      },
      {
        plan: "Max Loans",
        free: "1",
        starter: "5",
        gettingSerious: "15",
        fullTilt: "Unlimited",
        tooltip: {
          msg: (
            <span>
              Maximum Number of <br />
              Active Loans
            </span>
          ),
          trigger: "monthlyPrice",
        },
      },
    ],
    paymentProcessing: [
      {
        plan: "Credit Card Payments",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: false,
      },
      {
        plan: "ACH Payments",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: false,
      },
      {
        plan: "Individual ACH Deposits",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Each Payment deposits <br />
              individually without
              <br /> removing fees
            </span>
          ),
          trigger: "individualACHDeposits",
        },
      },
      {
        plan: "ACH Refunds",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Refund ACH Payments <br /> to Borrowers for <br />
              any reason
            </span>
          ),
          trigger: "ACHRefunds",
        },
      },
      {
        plan: "Two Bank Accounts",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Easily split Income <br />
              and Expenses between
              <br /> Bank Accounts
            </span>
          ),
          trigger: "twoBankAccounts",
        },
      },
      {
        plan: "ACH",
        subTextPlan: "Payment Transaction Fee",
        free: "$2",
        starter: "$1.50",
        gettingSerious: "$1.00",
        fullTilt: "$0.50",
        tooltip: {
          msg: (
            <span>
              A Simple, Flat, <br /> Per-Transaction Fee
            </span>
          ),
          trigger: "achTransactionFee",
        },
      },
      {
        plan: "ACH",
        subTextPlan: "Monthly Fee",
        free: "$0",
        starter: "$0",
        gettingSerious: "$0",
        fullTilt: "$0",
        tooltip: {
          msg: (
            <span>
              We don't charge you monthly <br />
              just for collecting ACH payments
            </span>
          ),
          trigger: "achMonthlyFee",
        },
      },
      {
        plan: "ACH",
        subTextPlan: "Required Reserves",
        free: "$0",
        starter: "$0",
        gettingSerious: "$0",
        fullTilt: "$0",
        tooltip: {
          msg: (
            <span>
              We never hold <br /> <b>Your Money</b> in reserve
            </span>
          ),
          trigger: "achRequiredReserves",
        },
      },
      {
        plan: "Loan Setup Fee ",
        free: "$0",
        starter: "$0",
        gettingSerious: "$0",
        fullTilt: "$0",
        tooltip: {
          msg: (
            <span>
              We never charge fees <br />
              to setup loans
            </span>
          ),
          trigger: "loanSetupFee",
        },
      },
      {
        plan: "Weekly ACH Processing",
        free: "$5,000+",
        starter: "$5,000+",
        gettingSerious: "$5,000+",
        fullTilt: "$5,000+",
        tooltip: {
          msg: (
            <span>
              Max limit per <br /> week for ACH
              <br /> transactions. "+" <br />
              indicates this limit <br />
              will increase with seasoning.
              <br />
            </span>
          ),
          trigger: "weeklyAchProcessing",
        },
      },
    ],
    features: [
      {
        plan: "Robust Email Follow up",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              60+ email types so your <br /> borrower will never be <br />
              able to say they "Forgot"!
            </span>
          ),
          trigger: "robustEmail",
        },
      },
      {
        plan: "Mobile Accessible",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: <span>Fully Accessible Mobile Site</span>,
          trigger: "mobileAccessible",
        },
      },
      {
        plan: "Cloud Infrastructure",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Entirely built on <br /> highly secure and <br /> resilient
              serverless <br /> AWS infrastructure
            </span>
          ),
          trigger: "cloudInfrastructure",
        },
      },
      {
        plan: "Loan Dashboard",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              See critical information for <br /> all your loans at a glance
            </span>
          ),
          trigger: "loanDashboard",
        },
      },
      {
        plan: "Loan Data Downloads",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              It's your data <br /> so downloading is easy
            </span>
          ),
          trigger: "loanDataDownloads",
        },
      },
      {
        plan: "Multiple Loans per Borrower",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Repeat Buyer? No Sweat. <br />
              Borrowers manage <br />
              all their loans from <br />
              one login
            </span>
          ),
          trigger: "perloan",
        },
      },
      {
        plan: "Borrower Session Recordings",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Gain massive insight <br /> from Borrower website
              <br /> session recordings
            </span>
          ),
          trigger: "borrowerSessionRecordings",
        },
      },
      {
        plan: "Borrower Assist System",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Help your Borrowers make <br /> Payments and more
            </span>
          ),
          trigger: "borrowerAssist",
        },
      },
      {
        plan: "Backdate Loans",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Easily backdate loans, <br /> payments, and fees
            </span>
          ),
          trigger: "backdateLoans",
        },
      },
      {
        plan: "Private Loan Notes",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Private per Loan <br /> Notes shared across
              <br /> your Team
            </span>
          ),
          trigger: "privateLoanNotes",
        },
      },
      {
        plan: "Custom Loan Tags",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Easily manage and sort your <br /> loans with custom tags
            </span>
          ),
          trigger: "customLoanTags",
        },
      },
      {
        plan: "One-Click Payoffs",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Everyone can generate <br />
              estimated payoff numbers
              <br /> in just one click
            </span>
          ),
          trigger: "oneclickpayoffs",
        },
      },
      {
        plan: "Multiple Borrowers per Loan",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: <span>Add up to 5 Borrowers to a Loan</span>,
          trigger: "multipleBorrowersperLoan",
        },
      },
      {
        plan: "Balloon Payments",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Add or Edit Balloon <br />
              Payments any time
            </span>
          ),
          trigger: "balloonPayments",
        },
      },

      {
        plan: "Interest Only Loans ",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Required Payments will <br />
              only be Interest and Fees
            </span>
          ),
          trigger: "interestOnlyLoans",
        },
      },
      {
        plan: "Arrears and Advance",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Calculate Loan Interest in
              <br /> either Arrears or Advance!
            </span>
          ),
          trigger: "arrearsAdvance",
        },
      },
      {
        plan: "Required Payment Override",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Manually set required <br />
              payments on a month
              <br /> by month basis
            </span>
          ),
          trigger: "requiredPaymentOverride",
        },
      },
      {
        plan: "Tax Report",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Detailed Tax Report <br />
              to help with 1098's
              <br /> and beyond!
            </span>
          ),
          trigger: "taxReport",
        },
      },
      {
        plan: "Borrower Payment Transparency",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Easily view the status
              <br /> of your borrower's payment
              <br /> account!
            </span>
          ),
          trigger: "borrowerPaymentTransparency",
        },
      },
      {
        plan: "Loan Transfers",
        free: <span className="badge bg-success text-center">✓</span>,
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Easily Transfer Loans to <br />
              another Lender
            </span>
          ),
          trigger: "loantransfers",
        },
      },
      // {
      //   plan: "Automated Loan Imports",
      //   free: "",
      //   starter: <span className="badge bg-success text-center">✓</span>,
      //   gettingSerious: <span className="badge bg-success text-center">✓</span>,
      //   fullTilt: <span className="badge bg-success text-center">✓</span>,
      //   tooltip: {
      //     msg: (
      //       <span>
      //         Importing Loans from other <br /> systems in easy with our <br />{" "}
      //         automated import tool
      //       </span>
      //     ),
      //     trigger: "automatedloanimports",
      //   },
      // },
      {
        plan: "Collect Downpayments",
        free: "",
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Collect Loan Downpayments <br /> via credit card
            </span>
          ),
          trigger: "collectDownpayments",
        },
      },
      {
        plan: "1098 Filing",
        free: "",
        starter: <span className="badge bg-success text-center">✓</span>,
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: <span>Easily File 1098 Tax Reports</span>,
          trigger: "1098Filing",
        },
      },

      {
        plan: "Team Member Accounts",
        free: "",
        starter: "",
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Team Members can access <br /> and manage your loans
              <br /> just as you can
            </span>
          ),
          trigger: "teammember1Accounts",
        },
      },
      {
        plan: "Partner Accounts",
        free: "",
        starter: "",
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              An account made for <br /> that Partner or Lender <br /> who wants
              a way to <br /> keep tabs on the details
            </span>
          ),
          trigger: "partner1Accounts",
        },
      },
      {
        plan: "Paperstac Integration",
        free: "",
        starter: "",
        gettingSerious: <span className="badge bg-success text-center">✓</span>,
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Instantly push your <br /> loan details to Paperstac!
            </span>
          ),
          trigger: "paperstacIntegration",
        },
      },
      {
        plan: "Automated Partner/Team Payments",
        free: "",
        starter: "",
        gettingSerious: "",
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Automatically send a portion <br /> of each payment directly to
              your
              <br /> Partner, Lender, or Team Member
            </span>
          ),
          trigger: "automatedPartner",
        },
      },
      {
        plan: "Borrower Dual Bank Accounts",
        free: "",
        starter: "",
        gettingSerious: "",
        fullTilt: <span className="badge bg-success text-center">✓</span>,
        tooltip: {
          msg: (
            <span>
              Borrower needs multiple bank <br /> accounts? No problem!
            </span>
          ),
          trigger: "borrowerDualBankAccounts",
        },
      },
    ],
    miscellaneousFees: [
      {
        plan: "ACH",
        subTextPlan: "Return Payment Fee",
        free: "$7",
        starter: "$7",
        gettingSerious: "$7",
        fullTilt: "$7",
        tooltip: {
          msg: (
            <span>
              Insufficient Funds, Closed <br />
              Accounts, Etc{" "}
            </span>
          ),
          trigger: "returnPaymentFee",
        },
      },
      {
        plan: "ACH",
        subTextPlan: "Unauthorized Return Payment Fee",
        free: "$10",
        starter: "$10",
        gettingSerious: "$10",
        fullTilt: "$10",
        tooltip: {
          msg: (
            <span>
              Payment Stopped, Canceled, <br />
              or Revoked by Sender
            </span>
          ),
          trigger: "unauthorizedReturnPaymentFee1",
        },
      },
      {
        plan: "1098 Filing",
        free: "",
        starter: "$5",
        gettingSerious: "$5",
        fullTilt: "$5",
        tooltip: {
          msg: (
            <span>
              Federal Filing, Mailing,
              <br /> and Online Access
            </span>
          ),
          trigger: "1098Filing-m",
        },
      },
      {
        plan: "Loan Transfer Fee",
        free: "$0",
        starter: "$0",
        gettingSerious: "$0",
        fullTilt: "$0",
        tooltip: {
          msg: (
            <span>
              Fee charged when <br /> accepting an incoming <br /> loan transfer
              interally <br /> within Terra Notes <br />
            </span>
          ),
          trigger: "loanTransferFee",
        },
      },
    ],
  };

  return (
    <div style={{ background: "rgb(231, 237, 243)" }}>
      <MetaTags
        title="Terra Notes - Transparent Pricing for Real Estate Loan & Note Servicing
          Software"
        description="Get clear, affordable pricing for Terra Notes' real estate loan and note management software. Experience top-tier note servicing without breaking the bank."
        url={`${process.env.REACT_APP_BASE_URL}/pricing`}
        image={`${process.env.REACT_APP_BASE_URL}/link-preview.png`}
      />

      <header style={{ height: width > 769 ? "75vh" : "100vh" }}>
        <div
          className="custom-overlay"
          style={{
            backgroundImage: `url(${"/images/wr-pricing-header.jpg"})`,
            backgroundSize: "cover",
            backgroundPosition: "bottom center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
          }}
        >
          <div className="custom-overlay" />
        </div>
        <div className="container h-100">
          <div className="d-flex h-100 text-center align-items-center">
            <div className="w-100 text-white home-customAbsoluteStyle px-2 ">
              <h1 className="text-white display-md-3 display-5 mt-3">
                COMPARE OUR PLANS
              </h1>
              <p className="text-white fs-4 mt-3">
                We have plans for every stage of your investing!
                <br />
                Whether you're just getting started or already have hundreds of
                notes.
                <br /> There's a plan with you in mind.
              </p>

              <NavLink
                className="btn btn-success fs-4 fw-bold px-5 mt-3"
                to="/Signup"
                style={{ width: "fit-content" }}
              >
                Start Your Free Trial
              </NavLink>

              <p className="text-white fs-4 my-4">
                Your first 30 days are on us.
                <br />
                Test drive the best loan management software on the market for
                30 days, no obligation!
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="mt-5">
        <div className="p-3 container">
          <div className="row row-cols-1 row-cols-md-2  row-cols-lg-4 g-3">
            <div className="col">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-title text-center align-items-center my-2">
                    Solo
                  </h4>
                </div>
                <div className="card-body p-0">
                  <div className=" p-2 border-bottom mb-3">
                    <h3 className="card-title text-center align-items-center my-2">
                      $10
                    </h3>
                    <p className="card-text text-center align-items-center">
                      Monthly Price
                    </p>
                    <p className="card-text text-center align-items-center fs-5 mb-3">
                      Max 1 Loan
                    </p>
                  </div>
                  <ul className="list-group list-group-flush p-2 py-3 ">
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Mobile Accessible
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Data Downloads{" "}
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Loans per Borrower
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Backdate Loans
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      One-Click Payoffs
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Borrowers per Loan
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Balloon Payments
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Transfers{" "}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-title text-center align-items-center my-2">
                    Starter
                  </h4>
                </div>

                <div className="card-body p-0">
                  <div className=" p-2 border-bottom mb-3">
                    <h3 className="card-title text-center align-items-center my-2">
                      $40
                    </h3>
                    <p className="card-text text-center align-items-center">
                      Monthly Price
                    </p>
                    <p className="card-text text-center align-items-center fs-5 mb-3">
                      Max 5 Loans
                    </p>
                  </div>
                  <ul className="list-group list-group-flush p-2 py-3 ">
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Mobile Accessible
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Data Downloads{" "}
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Loans per Borrower
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Backdate Loans
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      One-Click Payoffs
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Borrowers per Loan
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Balloon Payments
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Transfers{" "}
                    </li>
                    {/* <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Automated Loan Imports
                    </li> */}
                  </ul>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-title text-center align-items-center my-2">
                    Getting Serious
                  </h4>
                </div>

                <div className="card-body p-0">
                  <div className=" p-2 border-bottom mb-3">
                    <h3 className="card-title text-center align-items-center my-2">
                      $65
                    </h3>
                    <p className="card-text text-center align-items-center">
                      Monthly Price
                    </p>
                    <p className="card-text text-center align-items-center fs-5 mb-3">
                      Max 15 Loans
                    </p>
                  </div>
                  <ul className="list-group list-group-flush p-2 py-3 ">
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Mobile Accessible
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Data Downloads{" "}
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Loans per Borrower
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Backdate Loans
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      One-Click Payoffs
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Borrowers per Loan
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Balloon Payments
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Transfers{" "}
                    </li>
                    {/* <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Automated Loan Imports
                    </li> */}
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Team Member Accounts
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Partner Accounts
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-title text-center align-items-center my-2">
                    Full Tilt
                  </h4>
                </div>

                <div className="card-body p-0">
                  <div className=" p-2 border-bottom mb-3">
                    <h3 className="card-title text-center align-items-center my-2">
                      $135
                    </h3>
                    <p className="card-text text-center align-items-center">
                      Monthly Price
                    </p>
                    <p className="card-text text-center align-items-center fs-5 mb-3">
                      Unlimited Loans
                    </p>
                  </div>
                  <ul className="list-group list-group-flush p-2 py-3 ">
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Mobile Accessible
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Data Downloads{" "}
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Loans per Borrower
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Backdate Loans
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      One-Click Payoffs
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Multiple Borrowers per Loan
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Balloon Payments
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Loan Transfers{" "}
                    </li>
                    {/* <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Automated Loan Imports
                    </li> */}
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Team Member Accounts
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Partner Accounts
                    </li>
                    <li className="list-group-item border-0">
                      <span style={{ marginRight: 5 }}>
                        <CheckIcon className="text-success" size={20} />
                      </span>
                      Automated Partner/Team Payments
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex  justify-content-center mb-4">
        <NavLink
          className="btn btn-success fs-4 fw-bold px-5 mt-5"
          to="/Signup"
          style={{ width: "fit-content" }}
        >
          Start for Free!
        </NavLink>
      </div>
      <div className="d-flex justify-content-center fs-4 px-5">
        <p className="text-center">
          Your first 30 days are free, then upgrade to the plan that best fits
          your needs.
        </p>
      </div>
      <div className="p-3 text-center container">
        <div className="card shadow-none my-3">
          <div className="card-header">
            <h2 className="mt-4">PLANS & PRICING</h2>
          </div>

          <div className="card-body p-0 ">
            <div className="table-responsive table-billing-history text-start scrollView overflow-scroll w-full d-none d-md-block">
              <table className="table mb-0 fs-5" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th className="border-gray-200 text-center" scope="col">
                      Plans
                    </th>
                    <th
                      className="border-gray-200 text-center"
                      style={{ width: 200 }}
                      scope="col"
                    >
                      Solo
                    </th>
                    <th
                      className="border-gray-200 text-center"
                      style={{ width: 200 }}
                      scope="col"
                    >
                      Starter
                    </th>
                    <th
                      className="border-gray-200 text-center"
                      style={{ width: 200 }}
                      scope="col"
                    >
                      Getting Serious
                    </th>
                    <th
                      className="border-gray-200 text-center"
                      style={{ width: 200 }}
                      scope="col"
                    >
                      Full Tilt
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {planAndPricingTable?.basic?.map((row) => (
                    <tr>
                      <td className="text-center align-middle text-nowrap">
                        {row?.plan}{" "}
                        {row?.tooltip ? (
                          <>
                            <span data-tip data-for={row?.tooltip?.trigger}>
                              <QuestionIcon
                                className="text-primary"
                                size={20}
                              />
                            </span>
                            <ReactTooltip
                              place="TopCenter"
                              id={row?.tooltip?.trigger}
                            >
                              {row?.tooltip?.msg}
                            </ReactTooltip>
                          </>
                        ) : null}
                        {row?.subTextPlan ? (
                          <>
                            <br />
                            {row?.subTextPlan}
                          </>
                        ) : null}
                      </td>
                      {[
                        row?.free,
                        row?.starter,
                        row?.gettingSerious,
                        row?.fullTilt,
                      ].every(
                        (val) => typeof val === "string" && val === row?.free
                      ) ? (
                        <td className="text-center align-middle" colSpan={4}>
                          {row?.free}
                        </td>
                      ) : (
                        <>
                          <td className="text-center align-middle">
                            {row?.free}
                          </td>
                          <td className="text-center align-middle">
                            {row?.starter}
                          </td>
                          <td className="text-center align-middle">
                            {row?.gettingSerious}
                          </td>
                          <td className="text-center align-middle">
                            {row?.fullTilt}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  <tr>
                    <td
                      className="border-gray-200 text-center align-middle"
                      colSpan={5}
                      style={{
                        width: 200,
                        fontSize: "3vh",
                        backgroundColor: "#00000008",
                      }}
                    >
                      <b>Payment Processing </b>
                    </td>
                  </tr>

                  {planAndPricingTable?.paymentProcessing?.map((row) => (
                    <tr>
                      <td className="text-center align-middle text-nowrap">
                        {row?.plan}{" "}
                        {row?.tooltip ? (
                          <>
                            <span data-tip data-for={row?.tooltip?.trigger}>
                              <QuestionIcon
                                className="text-primary"
                                size={20}
                              />
                            </span>
                            <ReactTooltip
                              place="TopCenter"
                              id={row?.tooltip?.trigger}
                            >
                              {row?.tooltip?.msg}
                            </ReactTooltip>
                          </>
                        ) : null}
                        {row?.subTextPlan ? (
                          <>
                            <br />
                            {row?.subTextPlan}
                          </>
                        ) : null}
                      </td>
                      {[
                        row?.free,
                        row?.starter,
                        row?.gettingSerious,
                        row?.fullTilt,
                      ].every(
                        (val) => typeof val === "string" && val === row?.free
                      ) ? (
                        <td className="text-center align-middle" colSpan={4}>
                          {row?.free}
                        </td>
                      ) : (
                        <>
                          <td className="text-center align-middle">
                            {row?.free}
                          </td>
                          <td className="text-center align-middle">
                            {row?.starter}
                          </td>
                          <td className="text-center align-middle">
                            {row?.gettingSerious}
                          </td>
                          <td className="text-center align-middle">
                            {row?.fullTilt}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}

                  <tr>
                    <td
                      className="border-gray-200 text-center align-middle"
                      colSpan={5}
                      style={{
                        width: 200,
                        fontSize: "3vh",
                        backgroundColor: "#00000008",
                      }}
                    >
                      <b>Features </b>
                    </td>
                  </tr>

                  {planAndPricingTable?.features?.map((row) => (
                    <tr>
                      <td className="text-center align-middle text-nowrap">
                        {row?.plan}{" "}
                        {row?.tooltip ? (
                          <>
                            <span data-tip data-for={row?.tooltip?.trigger}>
                              <QuestionIcon
                                className="text-primary"
                                size={20}
                              />
                            </span>
                            <ReactTooltip
                              place="TopCenter"
                              id={row?.tooltip?.trigger}
                            >
                              {row?.tooltip?.msg}
                            </ReactTooltip>
                          </>
                        ) : null}
                        {row?.subTextPlan ? (
                          <>
                            <br />
                            {row?.subTextPlan}
                          </>
                        ) : null}
                      </td>
                      {[
                        row?.free,
                        row?.starter,
                        row?.gettingSerious,
                        row?.fullTilt,
                      ].every(
                        (val) => typeof val === "string" && val === row?.free
                      ) ? (
                        <td className="text-center align-middle" colSpan={4}>
                          {row?.free}
                        </td>
                      ) : (
                        <>
                          <td className="text-center align-middle">
                            {row?.free}
                          </td>
                          <td className="text-center align-middle">
                            {row?.starter}
                          </td>
                          <td className="text-center align-middle">
                            {row?.gettingSerious}
                          </td>
                          <td className="text-center align-middle">
                            {row?.fullTilt}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}

                  <tr onClick={handleToggle}>
                    <td
                      className="border-gray-200 text-center align-middle"
                      colSpan={5}
                      style={{
                        width: 200,
                        fontSize: "3vh",
                        backgroundColor: "#00000008",
                      }}
                    >
                      <b>Miscellaneous Fees </b>
                      {toggle ? (
                        <ChevronUpIcon size={30} className="ms-1 float-end" />
                      ) : (
                        <ChevronDownIcon size={30} className="ms-1 float-end" />
                      )}
                    </td>
                  </tr>

                  {toggle && (
                    <>
                      {planAndPricingTable?.miscellaneousFees?.map((row) => (
                        <tr>
                          <td className="text-center align-middle text-nowrap">
                            {row?.plan}{" "}
                            {row?.tooltip ? (
                              <>
                                <span data-tip data-for={row?.tooltip?.trigger}>
                                  <QuestionIcon
                                    className="text-primary"
                                    size={20}
                                  />
                                </span>
                                <ReactTooltip
                                  place="TopCenter"
                                  id={row?.tooltip?.trigger}
                                >
                                  {row?.tooltip?.msg}
                                </ReactTooltip>
                              </>
                            ) : null}
                            {row?.subTextPlan ? (
                              <>
                                <br />
                                {row?.subTextPlan}
                              </>
                            ) : null}
                          </td>
                          {[
                            row?.free,
                            row?.starter,
                            row?.gettingSerious,
                            row?.fullTilt,
                          ].every(
                            (val) =>
                              typeof val === "string" && val === row?.free
                          ) ? (
                            <td
                              className="text-center align-middle"
                              colSpan={4}
                            >
                              {row?.free}
                            </td>
                          ) : (
                            <>
                              <td className="text-center align-middle">
                                {row?.free}
                              </td>
                              <td className="text-center align-middle">
                                {row?.starter}
                              </td>
                              <td className="text-center align-middle">
                                {row?.gettingSerious}
                              </td>
                              <td className="text-center align-middle">
                                {row?.fullTilt}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
            <div className="table-responsive table-billing-history text-start scrollView overflow-scroll w-full d-block d-sm-block d-md-none">
              <table className="table table-sm" ref={tableRef}>
                <thead
                  className={
                    isSticky
                      ? "header stickyHeader"
                      : " header hidestickyHeader"
                  }
                  ref={headerRef}
                >
                  <tr>
                    <th
                      className="text-center align-middle"
                      scope="col"
                      style={{ width: "25%", padding: "10px 0px 10px" }}
                    >
                      Free!
                    </th>
                    <th
                      className="text-center align-middle"
                      scope="col"
                      style={{ width: "25%", padding: "10px 0px 10px" }}
                    >
                      Starter
                    </th>
                    <th
                      className="text-center align-middle"
                      scope="col"
                      style={{ width: "25%", padding: "10px 0px 10px" }}
                    >
                      Getting Serious
                    </th>
                    <th
                      className="text-center align-middle"
                      scope="col"
                      style={{ width: "25%", padding: "10px 0px 10px" }}
                    >
                      Full Tilt
                    </th>
                  </tr>
                </thead>
                <tbody className="content">
                  {planAndPricingTable?.basic?.map((row) => (
                    <>
                      <tr>
                        <th
                          className="text-center align-middle"
                          colspan="4"
                          style={{
                            width: "100%",
                            padding: "5px 0px 5px",
                            color: "#445649de",
                            border: "none",
                          }}
                        >
                          {row?.plan}{" "}
                          {row?.tooltip ? (
                            <>
                              <span data-tip data-for={row?.tooltip?.trigger}>
                                <QuestionIcon
                                  className="text-primary"
                                  size={20}
                                />
                              </span>
                              <ReactTooltip
                                place="TopCenter"
                                id={row?.tooltip?.trigger}
                              >
                                {row?.tooltip?.msg}
                              </ReactTooltip>
                            </>
                          ) : null}
                          {row?.subTextPlan ? (
                            <>
                              <br />
                              {row?.subTextPlan}
                            </>
                          ) : null}
                        </th>
                      </tr>
                      <tr>
                        {[
                          row?.free,
                          row?.starter,
                          row?.gettingSerious,
                          row?.fullTilt,
                        ].every(
                          (val) => typeof val === "string" && val === row?.free
                        ) ? (
                          <td
                            className="text-center align-middle"
                            colSpan={4}
                            style={{ padding: "0px 0px 10px" }}
                          >
                            {row?.free}
                          </td>
                        ) : (
                          <>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.free}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.starter}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.gettingSerious}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.fullTilt}
                            </td>
                          </>
                        )}
                      </tr>
                    </>
                  ))}
                  <tr>
                    <td
                      className="border-gray-200 text-center    align-middle"
                      colSpan="4"
                      style={{
                        fontSize: "1.35rem",
                        backgroundColor: "#00000008",
                      }}
                    >
                      <b>Payment Processing </b>
                    </td>
                  </tr>
                  {planAndPricingTable?.paymentProcessing?.map((row) => (
                    <>
                      <tr>
                        <th
                          className="text-center align-middle"
                          colspan="4"
                          style={{
                            width: "100%",
                            padding: "5px 0px 5px",
                            color: "#445649de",
                            border: "none",
                          }}
                        >
                          {row?.plan}{" "}
                          {row?.tooltip ? (
                            <>
                              <span data-tip data-for={row?.tooltip?.trigger}>
                                <QuestionIcon
                                  className="text-primary"
                                  size={20}
                                />
                              </span>
                              <ReactTooltip
                                place="TopCenter"
                                id={row?.tooltip?.trigger}
                              >
                                {row?.tooltip?.msg}
                              </ReactTooltip>
                            </>
                          ) : null}
                          {row?.subTextPlan ? (
                            <>
                              <br />
                              {row?.subTextPlan}
                            </>
                          ) : null}
                        </th>
                      </tr>
                      <tr>
                        {[
                          row?.free,
                          row?.starter,
                          row?.gettingSerious,
                          row?.fullTilt,
                        ].every(
                          (val) => typeof val === "string" && val === row?.free
                        ) ? (
                          <td
                            className="text-center align-middle"
                            colSpan={4}
                            style={{ padding: "0px 0px 10px" }}
                          >
                            {row?.free}
                          </td>
                        ) : (
                          <>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.free}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.starter}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.gettingSerious}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.fullTilt}
                            </td>
                          </>
                        )}
                      </tr>
                    </>
                  ))}
                  <tr>
                    <td
                      className="border-gray-200 text-center align-middle"
                      colSpan={5}
                      style={{
                        width: 200,
                        fontSize: "1.35rem",
                        backgroundColor: "#00000008",
                      }}
                    >
                      <b>Features </b>
                    </td>
                  </tr>
                  {planAndPricingTable?.features?.map((row) => (
                    <>
                      <tr>
                        <th
                          className="text-center align-middle"
                          colspan="4"
                          style={{
                            width: "100%",
                            padding: "5px 0px 5px",
                            color: "#445649de",
                            border: "none",
                          }}
                        >
                          {row?.plan}{" "}
                          {row?.tooltip ? (
                            <>
                              <span data-tip data-for={row?.tooltip?.trigger}>
                                <QuestionIcon
                                  className="text-primary"
                                  size={20}
                                />
                              </span>
                              <ReactTooltip
                                place="TopCenter"
                                id={row?.tooltip?.trigger}
                              >
                                {row?.tooltip?.msg}
                              </ReactTooltip>
                            </>
                          ) : null}
                          {row?.subTextPlan ? (
                            <>
                              <br />
                              {row?.subTextPlan}
                            </>
                          ) : null}
                        </th>
                      </tr>
                      <tr>
                        {[
                          row?.free,
                          row?.starter,
                          row?.gettingSerious,
                          row?.fullTilt,
                        ].every(
                          (val) => typeof val === "string" && val === row?.free
                        ) ? (
                          <td
                            className="text-center align-middle"
                            colSpan={4}
                            style={{ padding: "0px 0px 10px" }}
                          >
                            {row?.free}
                          </td>
                        ) : (
                          <>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.free}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.starter}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.gettingSerious}
                            </td>
                            <td
                              className="text-center align-middle"
                              style={{ width: "25%", padding: "0px 0px 10px" }}
                            >
                              {row?.fullTilt}
                            </td>
                          </>
                        )}
                      </tr>
                    </>
                  ))}

                  <tr onClick={handleToggle}>
                    <td
                      className="border-gray-200 text-center align-middle"
                      colSpan={5}
                      style={{
                        width: 200,
                        fontSize: "2.5vh",
                        backgroundColor: "#00000008",
                      }}
                    >
                      <b>Miscellaneous Fees </b>
                      {toggle ? (
                        <ChevronUpIcon size={30} className="ms-1 float-end" />
                      ) : (
                        <ChevronDownIcon size={30} className="ms-1 float-end" />
                      )}
                    </td>
                  </tr>
                  {toggle && (
                    <>
                      {planAndPricingTable?.miscellaneousFees?.map((row) => (
                        <>
                          <tr>
                            <th
                              className="text-center align-middle"
                              colspan="4"
                              style={{
                                width: "100%",
                                padding: "5px 0px 5px",
                                color: "#445649de",
                                border: "none",
                              }}
                            >
                              {row?.plan}{" "}
                              {row?.tooltip ? (
                                <>
                                  <span
                                    data-tip
                                    data-for={row?.tooltip?.trigger}
                                  >
                                    <QuestionIcon
                                      className="text-primary"
                                      size={20}
                                    />
                                  </span>
                                  <ReactTooltip
                                    place="TopCenter"
                                    id={row?.tooltip?.trigger}
                                  >
                                    {row?.tooltip?.msg}
                                  </ReactTooltip>
                                </>
                              ) : null}
                              {row?.subTextPlan ? (
                                <>
                                  <br />
                                  {row?.subTextPlan}
                                </>
                              ) : null}
                            </th>
                          </tr>
                          <tr>
                            {[
                              row?.free,
                              row?.starter,
                              row?.gettingSerious,
                              row?.fullTilt,
                            ].every(
                              (val) =>
                                typeof val === "string" && val === row?.free
                            ) ? (
                              <td
                                className="text-center align-middle"
                                colSpan={4}
                                style={{ padding: "0px 0px 10px" }}
                              >
                                {row?.free}
                              </td>
                            ) : (
                              <>
                                <td
                                  className="text-center align-middle"
                                  style={{
                                    width: "25%",
                                    padding: "0px 0px 10px",
                                  }}
                                >
                                  {row?.free}
                                </td>
                                <td
                                  className="text-center align-middle"
                                  style={{
                                    width: "25%",
                                    padding: "0px 0px 10px",
                                  }}
                                >
                                  {row?.starter}
                                </td>
                                <td
                                  className="text-center align-middle"
                                  style={{
                                    width: "25%",
                                    padding: "0px 0px 10px",
                                  }}
                                >
                                  {row?.gettingSerious}
                                </td>
                                <td
                                  className="text-center align-middle"
                                  style={{
                                    width: "25%",
                                    padding: "0px 0px 10px",
                                  }}
                                >
                                  {row?.fullTilt}
                                </td>
                              </>
                            )}
                          </tr>
                        </>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <h3 style={{ marginTop: 50 }}>
          Let us know if you have any questions! Support@TerraNotes.com
        </h3>
      </div>
    </div>
  );
}
